import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { routes } from "@routes";

import { getMessaging, isSupported, onMessage } from "firebase/messaging";
import { app, requestPermission } from "../../../../firebase/firebase";

import { filterRoutes, hasReservationChecked, isLogin } from "@utils/func";
import { getReservation } from "@apis/booking-room.api";

import { UserContext } from "@common/context/UserContext";
import { Box } from "@mui/material";

import { Switch } from "react-router-dom";

import { LoginPage } from "@auth-module/pages";

import { CHeader } from "@common/components/layout/Main";
import { CNotFound } from "@common/components/others";
import { PrivateRoute, PublicRoute } from "@common/components/routes";

const CMainLayout = () => {
  const _user = useSelector((state) => state.auth.user);

  const [user, setUser] = useState(null);
  const [noti, setNoti] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [route, setRoute] = useState(routes || []);

  const intervalIdRef = useRef(null);
  const notificationIntervalRef = useRef(null);

  const value = useMemo(
    () => ({
      user,
      setUser,
      noti,
      setNoti,
      loading,
      setLoading,
    }),
    [user, noti, loading]
  );

  // Hàm cleanup interval
  const clearAllIntervals = () => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current);
      notificationIntervalRef.current = null;
    }
  };

  // Hàm phát âm thanh
  const playNotificationSound = () => {
    const audio = new Audio("/noti.wav");
    audio.play().catch((err) => {
      console.warn("Audio play failed:", err);
    });
  };

  const getMessage = async () => {
    if (!(await isSupported())) return;

    const messaging = getMessaging();

    onMessage(messaging, (payload) => {
      if (payload) {
        setNoti(true);
        document.title = payload.notification.title;
        console.log("noti", payload);

        // Phát âm thanh khi có noti
        playNotificationSound();

        // Clear interval cũ nếu có
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
        }

        // Tạo interval mới để nháy title
        intervalIdRef.current = setInterval(() => {
          document.title =
            document.title === "CRM CMS"
              ? payload?.notification?.title || "CRM CMS"
              : "CRM CMS";
        }, 1000);
      }
    });
  };

  useEffect(() => {
    getMessage();

    return () => {
      clearAllIntervals();
    };
  }, []); // Chỉ chạy 1 lần khi component mount

  const handleClickAnywhere = () => {
    setLoading(true);
    setNoti(true);
  };

  // Fetch booking data chỉ khi cần thiết
  useEffect(() => {
    if (isLogin() && loading) {
      const fetchData = async () => {
        try {
          const data = await getReservation({
            pages: 0,
            page: 1,
            status: 0,
          });

          if (data) {
            setBookingData(data?.data);
          }
        } catch (error) {
          console.error("Error fetching reservation data:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [loading]);

  useEffect(() => {
    requestPermission();
  }, []);

  useEffect(() => {
    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current);
      notificationIntervalRef.current = null;
    }

    if (
      isLogin() &&
      bookingData?.amount > 0 &&
      hasReservationChecked(_user?.permissions)
    ) {
      let isOriginalTitle = true;
      document.title = "CRM CMS";

      notificationIntervalRef.current = setInterval(() => {
        // Phát âm thanh mỗi lần nháy
        playNotificationSound();

        if (isOriginalTitle) {
          document.title = "CRM CMS";
        } else {
          document.title = `Có ${bookingData?.amount} đơn đặt phòng mới!`;
        }
        isOriginalTitle = !isOriginalTitle;
      }, 1000);
    } else {
      // Reset title về mặc định nếu không có booking
      document.title = "CRM CMS";
    }

    return () => {
      if (notificationIntervalRef.current) {
        clearInterval(notificationIntervalRef.current);
        notificationIntervalRef.current = null;
      }
    };
  }, [isLogin(), bookingData?.amount, _user?.permissions]);

  useEffect(() => {
    if (_user?.permissions) {
      const routePer = filterRoutes(_user?.permissions, routes);
      setRoute(routePer);
    }
  }, [_user]);

  // Cleanup khi component unmount
  useEffect(() => {
    return () => {
      clearAllIntervals();
    };
  }, []);

  return (
    <>
      <UserContext.Provider value={value}>
        <Box
          component="section"
          className="c-layout"
          onClick={handleClickAnywhere}
        >
          <PrivateRoute component={CHeader} />
          <div className="c-body-content">
            <Switch>
              <PublicRoute
                restricted={true}
                component={LoginPage}
                path="/"
                exact
              />

              {route.map((item, index) => (
                <PrivateRoute
                  component={item.component}
                  path={item.path}
                  exact
                  key={index}
                />
              ))}

              <PrivateRoute component={CNotFound} path="*" />
              <PublicRoute component={CNotFound} path="*" />
            </Switch>
          </div>
        </Box>
      </UserContext.Provider>
    </>
  );
};

export default CMainLayout;
